import React from "react";
import { Grid } from "@material-ui/core";
import { useTranslate } from "react-admin";
import {
  useHarshStopEvents,
  useHarshBreakingEvents,
  useHarshTurnEvents,
  useHarshAccelerationEvents,
  useHarshSpeedingEvents,
  useAvgSpeeding,
  HarshDrivingReportsHookParams,
} from "../hooks/useHarshDrivingReportsHooks";
import ReportCardWidget from "../fuel-economy-report/ReportCardWidget";
import { round } from "../fuel-economy-report/chartUtils";

/** Unit for count: "time" when value is 1, else "times". */
const countUnit = (value: number): string => (value === 1 ? "time" : "times");

export interface HarshDrivingReportBaseMetricsWidgetProps
  extends HarshDrivingReportsHookParams {}

export const HarshDrivingReportBaseMetricsWidget: React.FC<HarshDrivingReportBaseMetricsWidgetProps> = ({
  fromDate,
  toDate,
  periodType,
}) => {
  const translate = useTranslate();
  const params = { fromDate, toDate, periodType };

  const harshStop = useHarshStopEvents(params);
  const harshBrake = useHarshBreakingEvents(params);
  const harshTurn = useHarshTurnEvents(params);
  const harshAccel = useHarshAccelerationEvents(params);
  const speedingCount = useHarshSpeedingEvents(params);
  const speedingAvg = useAvgSpeeding(params);

  const metrics: Array<{
    labelKey: string;
    value: string | number | null;
    unit: string;
    stops?: number[];
    isGrowing?: boolean;
    growth?: number;
    isReverse?: boolean;
    isLoading?: boolean;
    isError?: boolean;
  }> = [
    {
      labelKey: "harshDrivingReport.harshStop",
      value: harshStop.data?.value ?? null,
      unit: countUnit(harshStop.data?.value ?? 0),
      stops: harshStop.data?.stops ?? undefined,
      isGrowing: harshStop.data?.isGrowing,
      growth: harshStop.data?.growth,
      isLoading: harshStop.loading,
      isError: !!harshStop.error,
    },
    {
      labelKey: "harshDrivingReport.harshBrake",
      value: harshBrake.data?.value ?? null,
      unit: countUnit(harshBrake.data?.value ?? 0),
      stops: harshBrake.data?.stops ?? undefined,
      isGrowing: harshBrake.data?.isGrowing,
      growth: harshBrake.data?.growth,
      isLoading: harshBrake.loading,
      isError: !!harshBrake.error,
    },
    {
      labelKey: "harshDrivingReport.harshTurn",
      value: harshTurn.data?.value ?? null,
      unit: countUnit(harshTurn.data?.value ?? 0),
      stops: harshTurn.data?.stops ?? undefined,
      isGrowing: harshTurn.data?.isGrowing,
      growth: harshTurn.data?.growth,
      isLoading: harshTurn.loading,
      isError: !!harshTurn.error,
    },
    {
      labelKey: "harshDrivingReport.harshAcceleration",
      value: harshAccel.data?.value ?? null,
      unit: countUnit(harshAccel.data?.value ?? 0),
      stops: harshAccel.data?.stops ?? undefined,
      isGrowing: harshAccel.data?.isGrowing,
      growth: harshAccel.data?.growth,
      isLoading: harshAccel.loading,
      isError: !!harshAccel.error,
    },
    {
      labelKey: "harshDrivingReport.speedingCount",
      value: speedingCount.data?.value ?? null,
      unit: countUnit(speedingCount.data?.value ?? 0),
      stops: speedingCount.data?.stops ?? undefined,
      isGrowing: speedingCount.data?.isGrowing,
      growth: speedingCount.data?.growth,
      isReverse: true,
      isLoading: speedingCount.loading,
      isError: !!speedingCount.error,
    },
    {
      labelKey: "harshDrivingReport.speedingAverage",
      value:
        speedingAvg.data?.value != null ? round(speedingAvg.data.value) : null,
      unit: "mph",
      stops: speedingAvg.data?.stops ?? undefined,
      isGrowing: speedingAvg.data?.isGrowing,
      growth: speedingAvg.data?.growth,
      isReverse: true,
      isLoading: speedingAvg.loading,
      isError: !!speedingAvg.error,
    },
  ];

  return (
    <Grid container spacing={2} style={{ height: "100%", width: "100%" }}>
      {metrics.map((m) => (
        <Grid item xs={12} sm={6} key={m.labelKey} style={{ display: "flex" }}>
          <ReportCardWidget
            title={translate(m.labelKey, { _: m.labelKey })}
            value={m.value ?? "-"}
            unit={m.value != null ? m.unit : ""}
            stops={m.stops ?? undefined}
            isGrowing={m.isGrowing}
            growth={m.growth}
            isReverse={m.isReverse ?? false}
            isLoading={m.isLoading}
            isError={m.isError}
          />
        </Grid>
      ))}
    </Grid>
  );
};

export default HarshDrivingReportBaseMetricsWidget;
